import { del, get, post, put } from '../request';
import { toPage, type Page, type PageQuery } from './types';
import type { AgentProfile } from './agent';

/** 技能组（/skill-groups）：坐席单组绑定；队列超时策略在此配置 */
export interface SkillGroup {
  id: number;
  /** 显示名：界面展示与报表标签，创建后可改 */
  name: string;
  /**
   * 队列键（skill_group.code）：dialplan 的 `<code>_queue`、Redis 队列 key、路由/IVR/时段的
   * dest_value、机器人 transfer_queue 都按它引用。**创建后不可修改**（改名只改 name）。
   */
  code: string;
  description?: string;
  /** 排队超时秒数，默认 120 */
  queue_timeout_sec?: number;
  /** 超时去向：hangup=挂断 / survey=满意度调查 */
  timeout_action?: 'hangup' | 'survey';
  /** 满意度播报开关覆盖：null=跟随全局 / true=强制播报 / false=强制不播报 */
  satisfaction_enabled?: boolean | null;
  /** 排队播报文案 */
  announce?: string;
  /** 上级组 ID（null=根）。有子组的组为容器（部门），不可作路由队列目标 */
  parent_id?: number | null;
  /** 启停 */
  enabled?: boolean;
  /** 排队超时溢出目标组（当班运行时开关改的就是这一个字段；null=不溢出，按 timeout_action 处理） */
  overflow_group?: number | null;
  created_at?: string;
  /** 直属坐席数（GET /skill-groups，一次 GROUP BY 聚合；不含子组坐席） */
  direct_agent_count?: number;
  [k: string]: any;
}

/** 组织树节点（/skill-groups/tree） */
export interface SkillGroupNode extends SkillGroup {
  /** 直属+全部后代坐席数（注意与列表接口的 direct_agent_count 语义不同） */
  agent_count: number;
  /** 组内班组长/副组长（复用 supervisor 用户体系） */
  leaders: SkillGroupLeader[];
  children: SkillGroupNode[];
}

export interface SkillGroupLeader {
  user_id: number;
  username: string;
  name: string;
  /** leader=班组长 / deputy=副组长 / 空=普通主管 */
  rank: string;
}

export const TIMEOUT_ACTION_LABELS: Record<string, string> = {
  hangup: '挂断',
  survey: '满意度调查',
};

/** 满意度播报三态选项（null=跟随全局） */
export const SATISFACTION_OVERRIDE_LABELS: Record<string, string> = {
  follow: '跟随全局',
  on: '强制播报',
  off: '强制不播报',
};

/**
 * POST /duty/skill-groups/:id/overflow：当班运行时开关，只改「排队超时溢出目标」。
 * 守卫是运营监控页面码（operations.agents/queues/screen/cockpit，与队列暂停/广播同口径），不走 routing.manage；ACD 队列策略 30 秒内生效。
 */
export function setQueueOverflow(id: number, overflowGroupId?: number | null) {
  return post(`/duty/skill-groups/${id}/overflow`, { overflow_group_id: overflowGroupId ?? null });
}

/** GET /skill-groups（仪表盘等轮询场景传 silent，避免每轮失败弹 Toast） */
export async function listSkillGroups(q?: PageQuery, opts?: { silent?: boolean }): Promise<Page<SkillGroup>> {
  return toPage<SkillGroup>(await get('/skill-groups', { page: 1, pageSize: 100, ...(q || {}) }, { silent: !!opts?.silent }));
}

/** GET /skill-groups/tree —— 组织架构树（嵌套，agent_count 聚合子孙） */
export async function listSkillGroupTree(): Promise<SkillGroupNode[]> {
  return get('/skill-groups/tree');
}

/** POST /skill-groups —— 返回含服务端分配的队列键 code（可能与提交的名称不同：名称超长被截断或撞历史键加序号） */
export function createSkillGroup(v: Partial<SkillGroup>): Promise<SkillGroup> {
  return post<SkillGroup>('/skill-groups', v);
}

export function updateSkillGroup(id: number, v: Partial<SkillGroup>) {
  return put(`/skill-groups/${id}`, v);
}

/** 启停开关：只提交 { enabled }（白名单部分更新） */
export function setSkillGroupEnabled(id: number, enabled: boolean) {
  return put(`/skill-groups/${id}`, { enabled });
}

export function deleteSkillGroup(id: number) {
  return del(`/skill-groups/${id}`);
}

/** 技能组引用预览（GET /skill-groups/:id/refs）：删除前「被谁引用」，与删除端护栏同源 */
export interface SkillGroupRefItem {
  key: string;
  label: string;
  count: number;
  samples: string[];
  /** true=阻断删除；false=软引用（只提示） */
  hard: boolean;
}

export interface SkillGroupRefs {
  id: number;
  name: string;
  code: string;
  items: SkillGroupRefItem[];
  /** 阻断删除的文案（含类型与计数），空=可删 */
  blockers: string[];
}

/** GET /skill-groups/:id/refs —— 删除前引用预览 */
export function skillGroupRefs(id: number): Promise<SkillGroupRefs> {
  return get(`/skill-groups/${id}/refs`);
}

/** GET /skill-groups/:id/agents —— 组成员（组内坐席） */
export function listSkillGroupAgents(id: number): Promise<AgentProfile[]> {
  return get(`/skill-groups/${id}/agents`);
}

/**
 * PUT /skill-groups/:id/agents —— 全量替换组成员（坐席单组绑定）：
 * 列表中的坐席绑定到本组（含从其他组迁入），当前在组但不在列表中的坐席解绑。
 */
export function setSkillGroupAgents(id: number, agentIds: number[]) {
  return put(`/skill-groups/${id}/agents`, { agent_ids: agentIds });
}
